import {
    Popover,
    PopoverButtonProps,
    PopoverPanelProps,
    Transition,
} from '@headlessui/react';
import styles from './SoftPopup.module.sass';
import React, { ReactElement } from 'react';
import Icon from '@mdi/react';

// Button can be either a custom Popover.Button or an object (not component) with regular button props to override the default button

type SoftPopupWithCustomButton = {
    customButton: ReactElement<
        PopoverButtonProps<'button'>,
        typeof Popover.Button
    >;
    buttonProps?: never;
};

type SoftPopupWithButtonProps = {
    customButton?: never;
    buttonProps: PopoverButtonProps<'button'>;
};

type SoftPopupArrowPosition =
    'top_start' | 'top_end' | 'bottom_start' | 'bottom_end';

const SoftPopup = ({
    customButton,
    buttonProps,
    positioningClassName,
    panelClassName = '',
    arrowPosition = 'top_start',
    className,
    children,
}: {
    positioningClassName?: string;
    panelClassName?: string;
    arrowPosition?: SoftPopupArrowPosition;
    className?: string;
    children: PopoverPanelProps<'div'>['children'];
} & (SoftPopupWithButtonProps | SoftPopupWithCustomButton)) => {
    let arrowClassName = 'rounded-2xl ';
    switch (arrowPosition) {
        case 'top_start':
            arrowClassName += 'rounded-tl-lg';
            break;
        case 'top_end':
            arrowClassName += 'rounded-tr-lg';
            break;
        case 'bottom_start':
            arrowClassName += 'rounded-bl-lg';
            break;
        case 'bottom_end':
            arrowClassName += 'rounded-br-lg';
            break;
    }

    return (
        <Popover className={`${styles['softpopup']} ${className}`}>
            {customButton ? customButton : <Popover.Button {...buttonProps} />}
            <Transition
                as="div"
                enter="transition duration-100 ease-out"
                enterFrom="transform scale-95 opacity-0"
                enterTo="transform scale-100 opacity-200"
                leave="transition duration-75 ease-out"
                leaveFrom="transform scale-100 opacity-200"
                leaveTo="transform scale-95 opacity-0"
                className={`absolute ${
                    positioningClassName ?? 'top-0 left-0 origin-center'
                } z-200`}
            >
                <Popover.Panel
                    className={`${styles['softpopup__panel']} ${arrowClassName} ${panelClassName}`}
                >
                    {children}
                </Popover.Panel>
            </Transition>
        </Popover>
    );
};

export const SoftPopupDivider = () => (
    <div className={`${styles['softpopup__panel__divider']}`}>
        <hr className={`${styles['softpopup__panel__divider__line']}`} />
    </div>
);

export const SoftPopupButton = ({
    icon,
    onClick,
    children,
}: {
    icon: string;
    onClick: () => void;
    children: React.ReactNode;
}) => (
    <button
        className={`${styles['softpopup__panel__button']}`}
        onClick={onClick}
    >
        <Icon path={icon} size={1} />
        {children}
    </button>
);

export default SoftPopup;
